import { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, CalendarDays, TrendingUp } from "lucide-react";
import WeatherIcon from "./WeatherIcon";
import { Button } from "./ui/button";

export default function ExtendedForecast({ data, tempUnit = "fahrenheit" }) {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [viewMode, setViewMode] = useState("expanded"); // Start expanded by default
  
  const isCelsius = tempUnit === "celsius";
  
  const convertTemp = (tempF) => {
    if (isCelsius) {
      return Math.round((tempF - 32) * 5 / 9);
    }
    return Math.round(tempF);
  };
  
  // Respect the forecast provider's probability of precipitation exactly.
  const getAccurateRainChance = (day) => {
    if (day.pop === undefined || day.pop === null || Number.isNaN(Number(day.pop))) return 0;

    const normalizedPop = Number(day.pop) > 1 ? Number(day.pop) : Number(day.pop) * 100;
    return Math.max(0, Math.min(100, Math.round(normalizedPop)));
  };
  
  const getDayName = (timestamp, index) => {
    const date = new Date(timestamp * 1000);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    if (date.toDateString() === today.toDateString()) return "Today";
    if (date.toDateString() === tomorrow.toDateString()) return "Tomorrow";
    return date.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  };
  
  const checkScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };
  
  useEffect(() => {
    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, []);
  
  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 300;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
      setTimeout(checkScroll, 300);
    }
  };
  
  if (!data || data.length === 0) return null;
  
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CalendarDays className="w-5 h-5 text-[#00f2ea]" />
          <span className="font-semibold">15-Day Forecast</span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setViewMode(viewMode === "compact" ? "expanded" : "compact")}
            className="text-xs text-white/60 hover:text-white"
            data-testid="extended-forecast-view-toggle-button"
          >
            {viewMode === "compact" ? "Expand" : "Compact"}
          </Button>
        </div>
      </div>
      
      {/* Scrollable Forecast */}
      <div className="relative">
        {canScrollLeft && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => scroll('left')}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 h-8 w-8 rounded-full bg-black/60 backdrop-blur-sm hover:bg-black/80 border border-white/10"
            data-testid="extended-forecast-scroll-left-button"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
        )}
        
        {canScrollRight && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => scroll('right')}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 h-8 w-8 rounded-full bg-black/60 backdrop-blur-sm hover:bg-black/80 border border-white/10"
            data-testid="extended-forecast-scroll-right-button"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        )}
        
        <div
          ref={scrollRef}
          onScroll={checkScroll}
          className="flex gap-2 overflow-x-auto scrollbar-hide pb-2 px-1 snap-x snap-mandatory"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {data.map((day, idx) => (
            <motion.div
              key={day.dt}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.03 }}
              className={`flex-shrink-0 snap-start transition-all ${
                viewMode === "expanded" 
                  ? "w-32 glass-light rounded-2xl p-4" 
                  : "w-20 glass-light rounded-2xl p-3"
              } ${day.is_extended ? 'opacity-70 border border-dashed border-white/20' : ''}`}
              data-testid={`extended-forecast-day-card-${idx}`}
            >
              <p className={`text-white/50 mb-2 font-medium ${viewMode === "expanded" ? "text-sm" : "text-xs"}`}>
                {getDayName(day.dt, idx)}
              </p>
              
              <div className="flex justify-center mb-2">
                <WeatherIcon 
                  code={day.weather?.[0]?.icon || "01d"} 
                  size={viewMode === "expanded" ? 40 : 28}
                />
              </div>
              
              <div className="space-y-1 text-center">
                <p className={`font-bold ${viewMode === "expanded" ? "text-xl" : "text-base"}`}>
                  {convertTemp(day.temp?.max || day.temp?.day || 70)}°
                </p>
                <p className={`text-white/40 ${viewMode === "expanded" ? "text-sm" : "text-xs"}`}>
                  {convertTemp(day.temp?.min || (day.temp?.day - 10) || 60)}°
                </p>
              </div>
              
              {viewMode === "expanded" && (
                <div className="mt-3 pt-3 border-t border-white/10 space-y-1 text-xs text-white/60">
                  <div className="flex justify-between">
                    <span>Rain</span>
                    <span
                      className="text-[#00f2ea]"
                      data-testid={`extended-forecast-rain-chance-${idx}`}
                    >
                      {getAccurateRainChance(day)}%
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Humidity</span>
                    <span>{Math.round(day.humidity || 50)}%</span>
                  </div>
                  {day.wind_speed && (
                    <div className="flex justify-between">
                      <span>Wind</span>
                      <span>{Math.round(day.wind_speed)} mph</span>
                    </div>
                  )}
                </div>
              )}
              
              {getAccurateRainChance(day) > 30 && (
                <p className={`text-[#00f2ea] mt-1 ${viewMode === "expanded" ? "text-sm" : "text-xs"}`}>
                  {getAccurateRainChance(day)}%
                </p>
              )}
              
              {day.is_extended && (
                <div className="flex items-center justify-center mt-1">
                  <TrendingUp className="w-3 h-3 text-white/30" />
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
      
      {/* Extended forecast notice */}
      {data.some(d => d.is_extended) && (
        <p className="text-xs text-white/40 text-center flex items-center justify-center gap-1">
          <TrendingUp className="w-3 h-3" />
          Days 9-15 are extended predictions based on trends
        </p>
      )}
    </div>
  );
}
